var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, Fragment as _Fragment } from "@emotion/react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as pc from "playcanvas";
import { cardboardBoxMap, cardboardMaterialMap, tapeCenterPointMap, tapeEndsParts, tapeModelsMap, } from "./constants";
import { addTapeCollisionComponent, collisionComponentDefinitionsFromTapeModelID, } from "./collision-helper";
import { boxPanelDimensions, boxRenderHeight } from "./transform-helpers";
import { useAssetLoader } from "hooks/playcanvas/asset-loader";
import { useAppSelector, selectors } from "store";
import { objectPosition } from "3d/constants/common";
import { isLayerGreyedOut, isLayerHidden, layerTopWorldY } from "3d/box-stack";
import { getCardboardBoxWorldFootprint } from "3d/pallet";
import { applyMaterialTransforms, getMaterialAssets, greyOutEntity, highlightEntity, tintEntity, } from "3d/material-effects";
import { CameraViewId, Material, } from "3d/types";
import { tapeEndsParameterValues } from "api/tacton/constants";
import { useCollisionsContext } from "providers/collisions";
import { boxRootEntityName, boxAnchorEntityName, } from "3d/cardboard-box-entities";
// Re-exported for existing consumers that import the entity-name helpers from
// here; the source of truth is `3d/cardboard-box-entities`.
export { ROOT_ENTITY_NAME, CARDBOARD_BOX_ANCHOR_ENTITY_NAME, boxRootEntityName, boxAnchorEntityName, boxIdFromEntityName, } from "3d/cardboard-box-entities";
/*
 * Colours the boxes the cups are lifting, matching the pass/fail colours of
 * the floating force badges (theme green[500] / red[500]). Kept as literals
 * because PlayCanvas materials live outside the Emotion theme.
 */
var liftStatusTint = {
    secured: new pc.Color(0.35, 0.67, 0.15),
    "not-secured": new pc.Color(0.94, 0.31, 0.21),
};
export var CardboardBuilder = function (_a) {
    var app = _a.app, modelAssets = _a.modelAssets, materialAssets = _a.materialAssets;
    var setCollisionTriggers = useCollisionsContext().setCollisionTriggers;
    var cardboardBoxes = useAppSelector(selectors.selectCardboardBoxes);
    var isPalletized = useAppSelector(selectors.selectMultipleBoxesEnabled);
    var activeLayer = useAppSelector(selectors.selectActiveLayer);
    var selectedBoxId = useAppSelector(selectors.selectSelectedBoxId);
    var boxLiftStatuses = useAppSelector(selectors.selectBoxLiftStatuses);
    var isTopView = useAppSelector(selectors.selectCameraViewId) !==
        CameraViewId.INITIAL_3D_VIEW;
    // Collision-trigger parts (used for tape highlighting) aggregated across all
    // boxes. Each box instance reports its parts here; we push the union to the
    // collisions context whenever it changes.
    var collisionPartsByBox = useRef({});
    var reportCollisionParts = useCallback(function (boxId, parts) {
        collisionPartsByBox.current[boxId] = parts;
        setCollisionTriggers(Object.values(collisionPartsByBox.current).flat());
    }, [setCollisionTriggers]);
    var clearCollisionParts = useCallback(function (boxId) {
        delete collisionPartsByBox.current[boxId];
        setCollisionTriggers(Object.values(collisionPartsByBox.current).flat());
    }, [setCollisionTriggers]);
    return (_jsx(_Fragment, { children: cardboardBoxes.map(function (box) { return (_jsx(CardboardBoxInstance, { box: box, isPalletized: isPalletized, 
            // The layers stand stacked, the first on top at the pick plane
            // (see `3d/box-stack`); a layer that would cover the one being
            // worked on is hidden.
            isActiveLayer: box.layer === activeLayer, isHidden: isLayerHidden(box.layer, activeLayer), isGreyedOut: isLayerGreyedOut(box.layer, activeLayer, isTopView), topWorldY: layerTopWorldY(box.layer, boxRenderHeight, objectPosition.y), isSelected: box.id === selectedBoxId, liftStatus: boxLiftStatuses[box.id], app: app, modelAssets: modelAssets, materialAssets: materialAssets, reportCollisionParts: reportCollisionParts, clearCollisionParts: clearCollisionParts }, box.id)); }) }));
};
var CardboardBoxInstance = function (_a) {
    var app = _a.app, modelAssets = _a.modelAssets, materialAssets = _a.materialAssets, box = _a.box, isPalletized = _a.isPalletized, isActiveLayer = _a.isActiveLayer, isHidden = _a.isHidden, isGreyedOut = _a.isGreyedOut, topWorldY = _a.topWorldY, isSelected = _a.isSelected, liftStatus = _a.liftStatus, reportCollisionParts = _a.reportCollisionParts, clearCollisionParts = _a.clearCollisionParts;
    var sharedObjectSize = useAppSelector(selectors.selectObjectSize);
    var objectShapeId = useAppSelector(selectors.selectObjectShape);
    var loadAssets = useAssetLoader(app).loadAssets;
    var paperCoating = useAppSelector(selectors.selectPaperCoatingValue);
    var tape = useAppSelector(selectors.selectTapeParameter);
    var tapeEnds = useAppSelector(selectors.selectTapeEndsParameter);
    var _b = __read(useState(), 2), cardboardRootEntity = _b[0], setCardboardRootEntity = _b[1];
    var rootEntityName = boxRootEntityName(box.id);
    var anchorEntityName = boxAnchorEntityName(box.id);
    /* The panel dimensions, not the box's footprint: the assembled model's
     * mouldings sit outside its panels, so panels built at the full footprint
     * would push the finished box a centimetre over each edge — straight into
     * the neighbours a palletizing pattern puts there. The box's world X extent
     * is its size's `z` and its Z extent its `x` (see `getBoxWorldFootprint`). */
    var footprint = getCardboardBoxWorldFootprint(box, sharedObjectSize);
    var dimensions = useMemo(function () { return boxPanelDimensions(footprint.x, footprint.z, isPalletized); }, [footprint.x, footprint.z, isPalletized]);
    /*
     * Create this box's root entity + anchor. The anchor is an empty entity
     * centred on the box's logical position — the visual root's origin is a
     * corner of its panels — so drags read the resulting position off it when
     * they are persisted (see the picker's drag behaviors). Cup-over-box
     * assignment is pure geometry (`3d/cup-box-assignment`), so no collision
     * body is needed.
     */
    useEffect(function () {
        var entity = app.root.findByName(rootEntityName) || null;
        if (!entity) {
            entity = new pc.Entity(rootEntityName);
            app.root.addChild(entity);
        }
        var anchor = app.root.findByName(anchorEntityName);
        if (!anchor) {
            anchor = new pc.Entity(anchorEntityName);
            app.root.addChild(anchor);
        }
        setCardboardRootEntity(entity);
        return function () {
            anchor && anchor.destroy();
            entity.destroy();
            clearCollisionParts(box.id);
        };
    }, [app.root, rootEntityName, anchorEntityName, box.id, clearCollisionParts]);
    // Position and rotate the box (visual root + anchor). The visual
    // model's origin is a corner of its panels, so with no rotation the root
    // sits at box-center + (-width/2, 0, +length/2); the mouldings around the
    // panels are symmetric, so this centres the whole model on the box. When the box is rotated about its
    // centre, that corner offset rotates with it, so we rotate the offset by the
    // box rotation and place the root accordingly; rotating the root then turns
    // the whole model about the box centre. The panels hang below the root, so
    // the root's Y is the box's top face — the layer's place in the stack. The
    // anchor is centred on the box position and just needs the same rotation.
    useEffect(function () {
        var width = dimensions.width, length = dimensions.length;
        if (!cardboardRootEntity)
            return;
        var rotationQuat = new pc.Quat().setFromEulerAngles(0, box.rotation, 0);
        var centerToCorner = rotationQuat.transformVector(new pc.Vec3(width * 0.5, 0, -length * 0.5));
        cardboardRootEntity.setLocalEulerAngles(0, box.rotation, 0);
        cardboardRootEntity.setPosition(box.position.x - centerToCorner.x, topWorldY, box.position.z - centerToCorner.z);
        var anchor = app.root.findByName(anchorEntityName);
        anchor === null || anchor === void 0 ? void 0 : anchor.setLocalEulerAngles(0, box.rotation, 0);
        anchor === null || anchor === void 0 ? void 0 : anchor.setPosition(box.position.x, 0, box.position.z);
    }, [
        cardboardRootEntity,
        dimensions,
        box.position.x,
        box.position.z,
        box.rotation,
        topWorldY,
        app.root,
        anchorEntityName,
    ]);
    // Load all model/material assets and add them to each cardboard entity.
    // Also sets the scale and positions each object invidiually.
    var addModelsAndMaterialsToParts = useCallback(function (rootEntity, parts) {
        if (!paperCoating || !cardboardRootEntity)
            return;
        var assetsToLoad = [];
        // Remove all previous parts before generating new ones.
        for (var i = cardboardRootEntity.children.length - 1; i >= 0; i--) {
            cardboardRootEntity.children[i].destroy();
        }
        parts.forEach(function (part) {
            var modelAsset = part.modelAsset, entity = part.entity;
            entity.addComponent("model", {
                type: "asset",
            });
            rootEntity.addChild(part.entity);
            transformModel({ part: part, dimensions: dimensions });
            assetsToLoad.push(modelAsset);
        });
        var assetMaterialTransforms = getAssetMaterialTransforms(assetsToLoad, materialAssets, cardboardMaterialMap[paperCoating]);
        var updateModelAssets = function () {
            parts.forEach(function (part) {
                var entity = part.entity, modelAsset = part.modelAsset, scaleUVVec2 = part.scaleUVVec2;
                var uvScale = scaleUVVec2 && scaleUVVec2(dimensions);
                var model = entity.model;
                if (model) {
                    model.asset = 0;
                    model.asset = modelAsset;
                    applyMaterialTransforms(entity, assetMaterialTransforms, uvScale);
                }
                /* Only the live layer's tape can be touched by a cup. The layer
                 * beneath it is solid scenery: without this a cup over a gap in
                 * the top layer could light up, and warn about, tape it is not
                 * on. */
                if (isActiveLayer) {
                    addTapeCollisionComponent({
                        part: part,
                        dimensions: dimensions,
                        followsBox: isPalletized,
                    });
                }
            });
            /* Colour the box by lift status, then mark the selected box by
             * lightening it. Both have to happen here, after the assets have
             * loaded and the materials are in place — anywhere earlier and
             * `applyMaterialTransforms` would overwrite them. Selecting a box or
             * recalculating therefore rebuilds it, the same way changing the tape
             * or coating parameters does.
             *
             * Tint first, highlight second: the highlight lightens whatever
             * colour is there, so a selected lifted box stays green/red while
             * still reading as selected. A greyed-out box is background and
             * gets neither. */
            if (isGreyedOut) {
                greyOutEntity(cardboardRootEntity);
                return;
            }
            if (liftStatus) {
                tintEntity(cardboardRootEntity, liftStatusTint[liftStatus]);
            }
            if (isSelected) {
                highlightEntity(cardboardRootEntity);
            }
        };
        loadAssets(assetsToLoad, updateModelAssets);
    }, [
        cardboardRootEntity,
        dimensions,
        loadAssets,
        materialAssets,
        paperCoating,
        isSelected,
        liftStatus,
        isActiveLayer,
        isPalletized,
        isGreyedOut,
    ]);
    useEffect(function () {
        if (!cardboardRootEntity)
            return;
        /* A hidden layer gets no box model at all, and so no tape a cup could
         * collide with. That matters because the hidden layer is the one the
         * cups sit in, and its tape must not raise the cup-on-tape warning. */
        if (isHidden) {
            for (var i = cardboardRootEntity.children.length - 1; i >= 0; i--) {
                cardboardRootEntity.children[i].destroy();
            }
            clearCollisionParts(box.id);
            return;
        }
        if (!paperCoating || !tape || !objectShapeId || !tapeEnds)
            return;
        /* ----- Reset and create root entity and underlying parts ------*/
        var cardboardBox = cardboardBoxMap(objectShapeId, tapeCenterPointMap[tape]);
        var cardboardParts = generateParts(modelAssets, cardboardBox, box.id);
        var tapeModels = tapeModelsMap[tape];
        var shouldShowTapeEnds = tapeEnds.value === tapeEndsParameterValues.withTape;
        var tapeEndsModels = shouldShowTapeEnds ? tapeEndsParts : [];
        var tapeParts = generateParts(modelAssets, __spreadArray(__spreadArray([], __read(tapeModels), false), __read(tapeEndsModels), false), box.id);
        if (isActiveLayer) {
            var partsWithCollisions = __spreadArray(__spreadArray([], __read(cardboardParts), false), __read(tapeParts), false).filter(function (part) { return part.collisionComponentDefinition; });
            reportCollisionParts(box.id, partsWithCollisions);
        }
        else {
            clearCollisionParts(box.id);
        }
        /* ----- Apply all models and materials ------*/
        addModelsAndMaterialsToParts(cardboardRootEntity, __spreadArray(__spreadArray([], __read(cardboardParts), false), __read(tapeParts), false));
    }, [
        cardboardRootEntity,
        addModelsAndMaterialsToParts,
        paperCoating,
        tape,
        objectShapeId,
        modelAssets,
        tapeEnds,
        reportCollisionParts,
        clearCollisionParts,
        isActiveLayer,
        isHidden,
        box.id,
    ]);
    return null;
};
var getAssetMaterialTransforms = function (assetsToLoad, materialAssets, targetMaterial) {
    var materialTransforms = [
        {
            sourceMaterial: Material.MA_TESTOBJECT,
            targetMaterial: targetMaterial,
        },
    ];
    var assetMaterialTransforms = getMaterialAssets(materialAssets, materialTransforms);
    if (assetMaterialTransforms) {
        assetsToLoad.push.apply(assetsToLoad, __spreadArray([], __read(Object.values(assetMaterialTransforms)), false));
    }
    return assetMaterialTransforms;
};
var transformModel = function (_a) {
    var dimensions = _a.dimensions, part = _a.part;
    var entity = part.entity, scaleVec3 = part.scaleVec3, translateVec3 = part.translateVec3, rotateVec3 = part.rotateVec3;
    if (scaleVec3) {
        var scale = scaleVec3(dimensions);
        entity.setLocalScale(scale);
    }
    if (translateVec3) {
        var translation = translateVec3(dimensions);
        entity.translateLocal(translation);
    }
    if (rotateVec3) {
        var rotation = rotateVec3(dimensions);
        entity.setLocalEulerAngles(rotation);
    }
};
// Generate entities and assets for each individual part used to build the cardboard box.
// The box id is folded into each entity name so parts are uniquely identified per box.
// Collision detection records the colliding entity by name and the highlight effect
// matches triggers by name, so without the box id a tape hit on one box would highlight
// the same-named tape on every box.
var generateParts = function (modelAssets, rawCardboardParts, boxId) {
    return rawCardboardParts.map(function (part, index) {
        var modelId = part.modelId, transforms = __rest(part, ["modelId"]);
        var entityName = "".concat("Cardboard-Part" + modelId, "-").concat(index, "-").concat(boxId);
        return __assign({ modelAsset: modelAssets[modelId], entity: new pc.Entity(entityName), modelId: modelId, collisionComponentDefinition: collisionComponentDefinitionsFromTapeModelID[modelId] }, transforms);
    });
};
